"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.todoOpenApiDocs = exports.TodoSchema = exports.TodoItemSchema = exports.TodoItemValueSchema = void 0;
const ErrorResponse = {
    type: 'object',
    properties: {
        success: { type: 'boolean', example: false },
        error: { type: 'string', description: 'Human-readable error message' },
        error_code: { type: 'string', description: 'Error code for programmatic handling' }
    }
};
// ITodoItemValue — per-participant done state for a todo item
exports.TodoItemValueSchema = {
    type: 'object',
    description: 'Per-participant completion state for a todo item (ITodoItemValue)',
    properties: {
        id: { type: 'number', description: 'Value record identifier' },
        done: { type: 'boolean', description: 'Whether this participant has completed the todo item' },
        participant: {
            type: 'object',
            description: 'The participant this value belongs to (IParticipant)',
            properties: {
                id: { type: 'number' },
                uuid: { type: 'string' },
                name: { type: 'string' },
                userIdentity: { type: 'boolean' }
            }
        },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    }
};
// ITodoItem
exports.TodoItemSchema = {
    type: 'object',
    description: 'A single actionable item within a todo (ITodoItem)',
    properties: {
        id: { type: 'number', description: 'Todo item identifier' },
        title: { type: 'string', description: 'Todo item title' },
        done: {
            type: 'boolean',
            description: 'Computed done state — true if the current participant (or user) has completed this item (resolved from ITodoItemValue)'
        },
        order: { type: 'integer', description: 'Display order of the todo item' },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    },
    required: ['title']
};
// ITodo
exports.TodoSchema = {
    type: 'object',
    description: 'A todo list with its items (ITodo)',
    properties: {
        id: { type: 'number', description: 'Todo identifier' },
        title: { type: 'string', description: 'Todo title' },
        done: { type: 'boolean', description: 'Whether the entire todo is marked complete' },
        owner: { type: 'string', description: 'ID of the owning user' },
        creator: {
            type: 'object',
            description: 'User who created this todo',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                email: { type: 'string' }
            }
        },
        items: {
            type: 'array',
            description: 'Todo items (ITodoItem[])',
            items: exports.TodoItemSchema
        },
        created_at: { type: 'string', format: 'date-time' },
        updated_at: { type: 'string', format: 'date-time' }
    },
    required: ['title']
};
const TodoPayloadBody = {
    type: 'object',
    description: 'Todo creation/update payload (ITodoPayload)',
    properties: {
        title: { type: 'string', description: 'Todo title', required: true },
        done: { type: 'boolean', description: 'Whether the todo is complete', default: false },
        owner: { type: 'string', description: 'Owner user ID' },
        items: {
            type: 'array',
            description: 'Todo items to create/replace',
            items: {
                type: 'object',
                properties: {
                    title: { type: 'string', description: 'Item title' },
                    description: { type: 'string', description: 'Item description' },
                    done: { type: 'boolean', default: false },
                    order: { type: 'integer', description: 'Display order' }
                },
                required: ['title']
            }
        }
    },
    required: ['title']
};
exports.todoOpenApiDocs = {
    'todo.list': {
        description: 'List todos for the current user with optional search query and pagination',
        payload: {
            params: {
                type: 'object',
                properties: {
                    q: { type: 'string', description: 'Optional search query to filter todos by title' }
                }
            },
            query: {
                type: 'object',
                properties: {
                    p: { type: 'integer', description: 'Page number (1-indexed)', minimum: 1, default: 1 },
                    per_page: { type: 'integer', description: 'Items per page', minimum: 1, maximum: 100, default: 20 }
                }
            }
        },
        responses: {
            200: {
                description: 'Paginated list of todos',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: { type: 'array', items: exports.TodoSchema },
                        count: { type: 'integer', description: 'Total number of todos matching the query' }
                    }
                }
            },
            500: { description: 'Failed to fetch todos', returnParams: ErrorResponse }
        }
    },
    'todo.get': {
        description: 'Get a single todo by ID (must belong to the current user)',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'number', description: 'Todo ID', required: true }
                }
            }
        },
        responses: {
            200: {
                description: 'Todo details with items',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TodoSchema
                    }
                }
            },
            404: { description: 'Todo not found', returnParams: ErrorResponse },
            500: { description: 'Failed to fetch todo', returnParams: ErrorResponse }
        }
    },
    'todo.create': {
        description: 'Create a new todo with optional items',
        payload: { body: TodoPayloadBody },
        responses: {
            200: {
                description: 'Created todo with items',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TodoSchema
                    }
                }
            },
            500: { description: 'Failed to create todo', returnParams: ErrorResponse }
        }
    },
    'todo.update': {
        description: 'Update an existing todo and replace all its items',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'number', description: 'Todo ID to update', required: true }
                }
            },
            body: TodoPayloadBody
        },
        responses: {
            200: {
                description: 'Updated todo with items',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TodoSchema
                    }
                }
            },
            404: { description: 'Todo not found', returnParams: ErrorResponse },
            500: { description: 'Failed to update todo', returnParams: ErrorResponse }
        }
    },
    'todo.delete': {
        description: 'Delete a todo and all its items (must belong to the current user)',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: { type: 'number', description: 'Todo ID to delete', required: true }
                }
            }
        },
        responses: {
            200: {
                description: 'Todo deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: { success: { type: 'boolean', example: true } }
                }
            },
            404: { description: 'Todo not found', returnParams: ErrorResponse },
            500: { description: 'Failed to delete todo', returnParams: ErrorResponse }
        }
    },
    'todo-item.update': {
        description: 'Toggle the done state of a todo item for the current user or a specific participant. Uses ITodoItemValue to store per-participant state.',
        payload: {
            body: {
                type: 'object',
                description: 'Toggle payload',
                properties: {
                    itemId: {
                        type: 'string',
                        description: 'ID of the TodoItem to toggle',
                        required: true
                    },
                    participantUuid: {
                        type: 'string',
                        description: 'Participant UUID for per-participant tracking (optional — defaults to current user identity participant)'
                    }
                },
                required: ['itemId']
            }
        },
        responses: {
            200: {
                description: 'Updated todo after toggling the item',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.TodoSchema
                    }
                }
            },
            404: { description: 'Todo item or participant not found', returnParams: ErrorResponse },
            500: { description: 'Failed to toggle todo item', returnParams: ErrorResponse }
        }
    }
};
